/**
 * InvoiceChargesEditor — the "Optional charges" block of the invoice
 * editor (Travel · Fuel Surcharge · Tools & Equipment · Other),
 * extracted from InvoiceEditorDialog.jsx (Jun 2026).
 * All state stays in the parent dialog and arrives as props.
 */
import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { CustomColumnInputs } from '../ColumnLayoutEditor';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Trash2, Truck, ChevronDown } from 'lucide-react';

export const CHARGE_KIND_LABELS = {
  travel: 'Travel',
  fuel_surcharge: 'Fuel Surcharge',
  tools_equipment: 'Tools & Equipment',
  equipment: 'Equipment (rental / hourly)',
  labor: 'Additional labour',
  energy: 'Energy / utilities',
  service: 'Service fee',
  other: 'Other',
};

export const InvoiceChargesEditor = ({
  charges,
  chargeSuggestions,
  chargeMenuOpen,
  setChargeMenuOpen,
  addCharge,
  removeCharge,
  updateCharge,
  money,
  columns = [],
}) => (
  <div data-testid="invoice-charges-block" className="rounded-lg border border-[#2D3748] bg-[#0F1923]/40 p-3">
    <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
      <div className="flex items-center gap-2">
        <Truck className="w-4 h-4 text-amber-400" />
        <h4 className="text-sm font-semibold text-slate-200">
          Optional charges
        </h4>
        <span className="text-[11px] text-slate-500">
          Travel · Fuel · Tools · Equipment · Labour · Energy · Service fee · Other
        </span>
      </div>
      <div className="relative">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => setChargeMenuOpen((v) => !v)}
          className="text-xs h-8 border-amber-500/40 text-amber-200 hover:bg-amber-500/10"
          data-testid="invoice-charges-add-btn"
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> Add optional charge
          <ChevronDown className="w-3.5 h-3.5 ml-1" />
        </Button>
        {chargeMenuOpen && (
          <div
            className="absolute right-0 mt-1 w-72 rounded-md border border-[#2D3748] bg-[#0F1923] shadow-xl z-30"
            data-testid="invoice-charges-menu"
          >
            {(chargeSuggestions.length === 0
              ? [
                  { kind: 'travel', description: 'Travel time', quantity: 0, unit_price: 0, note: '' },
                  { kind: 'fuel_surcharge', description: 'Fuel surcharge', quantity: 1, unit_price: 0, note: '' },
                  { kind: 'tools_equipment', description: 'Tools & equipment', quantity: 1, unit_price: 0, note: '' },
                  { kind: 'equipment', description: 'Equipment rental (per hour/day)', quantity: 1, unit_price: 0, note: '' },
                  { kind: 'labor', description: 'Additional labour', quantity: 1, unit_price: 0, note: '' },
                  { kind: 'energy', description: 'Energy / utilities', quantity: 1, unit_price: 0, note: '' },
                  { kind: 'service', description: 'Service fee', quantity: 1, unit_price: 0, note: '' },
                  { kind: 'other', description: 'Other charge', quantity: 1, unit_price: 0, note: '' },
                ]
              : chargeSuggestions
            ).map((s) => (
              <button
                key={s.kind}
                type="button"
                onClick={() => addCharge(s)}
                className="w-full text-left px-3 py-2 hover:bg-amber-500/10 border-b border-[#1F2937] last:border-b-0"
                data-testid={`invoice-charges-menu-item-${s.kind}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-slate-200">
                    {CHARGE_KIND_LABELS[s.kind] || 'Charge'}
                  </span>
                  {s.kind === 'travel' && s.quantity > 0 && (
                    <span className="text-[11px] font-mono text-amber-300">
                      {Number(s.quantity).toFixed(2)} hr @ ${Number(s.unit_price).toFixed(2)}
                    </span>
                  )}
                </div>
                {s.note && (
                  <p className="text-[11px] text-slate-500 mt-0.5">{s.note}</p>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>

    {charges.length === 0 ? (
      <div className="text-xs text-slate-500 py-2">
        No optional charges added. Use the dropdown above to add Travel time (auto-priced from logged segments), a Fuel Surcharge, Tools &amp; Equipment, or any other extra line.
      </div>
    ) : (
      <div className="space-y-2">
        {charges.map((c, idx) => (
          <div
            key={c.id || idx}
            className="grid grid-cols-12 gap-2 items-center bg-[#0B141C] border border-[#2D3748] rounded-md p-2"
            data-testid={`invoice-charge-row-${idx}`}
          >
            <div className="col-span-2">
              <Select
                value={c.kind || 'other'}
                onValueChange={(v) => updateCharge(idx, 'kind', v)}
              >
                <SelectTrigger
                  className="h-8 bg-[#1A2433] border-[#2D3748] text-white text-xs"
                  data-testid={`invoice-charge-kind-${idx}`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="travel">Travel</SelectItem>
                  <SelectItem value="fuel_surcharge">Fuel Surcharge</SelectItem>
                  <SelectItem value="tools_equipment">Tools &amp; Equipment</SelectItem>
                  <SelectItem value="equipment">Equipment (rental / hourly)</SelectItem>
                  <SelectItem value="labor">Additional labour</SelectItem>
                  <SelectItem value="energy">Energy / utilities</SelectItem>
                  <SelectItem value="service">Service fee</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Input
              value={c.description || ''}
              onChange={(e) => updateCharge(idx, 'description', e.target.value)}
              placeholder={CHARGE_KIND_LABELS[c.kind || 'other']}
              className="col-span-5 h-8 bg-[#1A2433] border-[#2D3748] text-white text-xs"
              data-testid={`invoice-charge-description-${idx}`}
            />
            <Input
              type="number"
              step="0.01"
              min="0"
              value={c.quantity ?? ''}
              onChange={(e) => updateCharge(idx, 'quantity', e.target.value)}
              placeholder="Qty"
              className="col-span-1 h-8 bg-[#1A2433] border-[#2D3748] text-white text-xs text-right"
              data-testid={`invoice-charge-quantity-${idx}`}
            />
            <Input
              type="number"
              step="0.01"
              min="0"
              value={c.unit_price ?? ''}
              onChange={(e) => updateCharge(idx, 'unit_price', e.target.value)}
              placeholder="Unit price"
              className="col-span-2 h-8 bg-[#1A2433] border-[#2D3748] text-white text-xs text-right"
              data-testid={`invoice-charge-unit-price-${idx}`}
            />
            <div
              className="col-span-1 text-right text-xs font-mono text-slate-300"
              data-testid={`invoice-charge-line-total-${idx}`}
            >
              {money(((parseFloat(c.quantity) || 0) * (parseFloat(c.unit_price) || 0)))}
            </div>
            <div className="col-span-1 flex justify-end">
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={() => removeCharge(idx)}
                className="h-7 w-7 text-rose-400 hover:bg-rose-500/10"
                data-testid={`invoice-charge-remove-${idx}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
            {columns.some((c) => c.source === 'custom') && (
              <div className="col-span-12">
                <CustomColumnInputs columns={columns} values={c.custom_fields} onChange={(v) => updateCharge(idx, 'custom_fields', v)} testIdPrefix={`invoice-charge-cf-${idx}`} compact />
              </div>
            )}
          </div>
        ))}
      </div>
    )}
  </div>
);

export default InvoiceChargesEditor;
